import { createContext, useContext, useEffect, useState, useRef } from "react";
import i18n from '../i18n/index.js';
import RemoteStorageUnit from "../lib/remoteStorage";
import { useAccount } from "./accountContext";
import { usePopup } from "./popupContext";
import SingleProductOnlyPopup from "../sections/cartPopup/SingleProductOnlyPopup";
import { NexApi } from "../lib/neuroexchangeApi";
import { createMetricAmountFormater } from "src/lib/formating.js";
const MAX_TOKENS_PER_PROJECT_IN_CART = 100;

const cartContex = createContext();

export function CartItem({
  contractId,
  value,
  premium,
  currency,
  parameters,
  count,
  nickname,
  variables,
  buyInstructions,
  projectId,
  projectKey,
  projectStatsId,
  projectTitle,
}) {
  return {
    // potentialy multiple ways of buying ex transferring insted of creating so id:ing might not be contract id
    get id() {
      // Use projectId if available to differentiate products sharing a template
      return projectId || contractId;
    },
    get displayName() {
      return nickname || contractId;
    },
    buyInstructions,
    variables,
    value,
    premium: Number(premium || 0),
    currency,
    contractId,
    parameters,
    count: count,
    projectId,
    projectKey,
    projectStatsId,
    projectTitle,
  };
}

const storageUnit = RemoteStorageUnit({
  localname: "cart-unit",
  loadOnInit: false,
});

export default function CartProvider({ children }) {
  const accountContext = useAccount();
  const popupContext = usePopup();
  const [cartItems, setCartItems] = useState([]);
  const saveDebounceRef = useRef(null);
  const guestCartKey = 'guestCartItems';
  const pendingCheckoutKey = 'pendingCheckout';

  const [projects, setProjects] = useState([]);
  const normalizeCartItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    const first = items[0];
    const parsedCount = Math.max(1, Math.min(MAX_TOKENS_PER_PROJECT_IN_CART, Math.floor(Number(first?.count || 1))));
    return [{ ...first, count: parsedCount }];
  };
  useEffect(() => {
    let mounted = true;
    const loadProjects = async () => {
      try {
        const localization = i18n?.resolvedLanguage || i18n?.language || 'pt-BR';
        const apiProjects = await NexApi.getProjects(localization);
        if (mounted) setProjects(Array.isArray(apiProjects) ? apiProjects : []);
      } catch (e) {
        if (mounted) setProjects([]);
        console.warn('[cartContext] Failed to load projects', e);
      }
    };

    loadProjects();
    return () => { mounted = false; };
  }, []);

  const getItemUnitPrice = (item) => {
    return Number(item?.value || 0) + Number(item?.premium || 0);
  };

  const getProjectForItem = (item) => {
    return projects.find((p) => (
      (item?.projectId && (String(p?.projectId) === String(item.projectId) || String(p?.token?.projectId) === String(item.projectId))) ||
      (item?.projectTitle && (String(p?.page?.title) === String(item.projectTitle) || String(p?.token?.projectLabel) === String(item.projectTitle)))
    )) || null;
  };

  const getEffectiveMaxForItem = (item) => {
    const project = getProjectForItem(item);

    if (!project) return undefined;
    const tokensLeft = Number(project?.token?.tokensLeft);
    const quantity = Number(project?.token?.quantity);
    const unitPrice = getItemUnitPrice(item);
    const priceBasedMax = unitPrice > 0 && Number.isFinite(tokensLeft)
      ? Math.max(0, Math.floor(tokensLeft))
      : undefined;
    const supplyCap = Number.isFinite(Number(quantity))
      ? Math.max(0, Math.floor(Number(quantity)))
      : undefined;
    if (priceBasedMax === undefined && supplyCap === undefined) return undefined;
    if (priceBasedMax === undefined) return supplyCap;
    if (supplyCap === undefined) return priceBasedMax;
    return Math.min(priceBasedMax, supplyCap);
  };

  // to decreese the count just set the item arguments count to a negaqtive value
  const addToCart = (item) => {
    // Enforce single-product rule: if any existing item has a different id, block and show popup
    const hasDifferent = cartItems.length > 0 && cartItems.some((ci) => ci.id !== item.id);
    if (hasDifferent) {
      try {
        popupContext.transitionScreen({ element: <SingleProductOnlyPopup /> });
      } catch (_) {
        // fail-safe: ignore popup errors
      }
      return false; // do not modify cart
    }

    // Merge counts for the same product id
    const currentItemRep = cartItems.findIndex((iItem) => iItem.id === item.id);
    const currentAmount = currentItemRep !== -1 ? cartItems[currentItemRep].count : 0;
    const requested = Math.max(0, Math.floor(Number(item.count || 0)));
    const requestedWithinCartLimit = Math.min(requested, MAX_TOKENS_PER_PROJECT_IN_CART);
    if (requestedWithinCartLimit < requested) {
      try { popupContext.toast?.(i18n.t('common:productCard.maxAmountExceeded', 'Maximum available is {{max}}.', { max: new Intl.NumberFormat('pt-BR').format(MAX_TOKENS_PER_PROJECT_IN_CART) })); } catch (_) { void 0; }
    }

    // Enforce upper limit
    const max = getEffectiveMaxForItem(item);
    const cappedMax = max !== undefined
      ? Math.min(max, MAX_TOKENS_PER_PROJECT_IN_CART)
      : MAX_TOKENS_PER_PROJECT_IN_CART;
    if (max !== undefined) {
      const remaining = Math.max(0, cappedMax - currentAmount);
      if (remaining <= 0) {
        try { popupContext.toast?.(i18n.t('common:productCard.noUnitsAvailable', 'No units available to add.')); } catch (_) { void 0; }
        return false;
      }
      const clampedAdd = Math.min(requestedWithinCartLimit, remaining);
      if (clampedAdd < requestedWithinCartLimit) {
        try { popupContext.toast?.(i18n.t('common:productCard.maxAmountExceeded', 'Maximum available is {{max}}.', { max: new Intl.NumberFormat('pt-BR').format(cappedMax) })); } catch (_) { void 0; }
      }
      item.count = clampedAdd + currentAmount;
    } else {
      const cartRemaining = Math.max(0, MAX_TOKENS_PER_PROJECT_IN_CART - currentAmount);
      const clampedAdd = Math.min(requestedWithinCartLimit, cartRemaining);
      if (clampedAdd < requestedWithinCartLimit) {
        try { popupContext.toast?.(i18n.t('common:productCard.maxAmountExceeded', 'Maximum available is {{max}}.', { max: new Intl.NumberFormat('pt-BR').format(MAX_TOKENS_PER_PROJECT_IN_CART) })); } catch (_) { void 0; }
      }
      if (clampedAdd <= 0) {
        try { popupContext.toast?.(i18n.t('common:productCard.noUnitsAvailable', 'No units available to add.')); } catch (_) { void 0; }
        return false;
      }
      item.count = clampedAdd + currentAmount;
    }
    setCartItems([item, ...cartItems.filter((_, i) => i !== currentItemRep)]);
    return true;
  };

  const removeFromCart = (item) => {
    setCartItems(cartItems.filter((iItem) => iItem.id !== item.id));
  };

  const updateItemCount = (id, nextCount) => {
    const parsedNext = Number(nextCount || 0);

    setCartItems(prev => {
      const target = prev.find(ci => ci.id === id);
      const max = target ? getEffectiveMaxForItem(target) : undefined;
      const cappedMax = max !== undefined
        ? Math.min(max, MAX_TOKENS_PER_PROJECT_IN_CART)
        : MAX_TOKENS_PER_PROJECT_IN_CART;
      const finalCount = Math.min(parsedNext, cappedMax);
      if (finalCount < parsedNext) {
        try { popupContext.toast?.(i18n.t('common:productCard.maxAmountExceeded', 'Maximum available is {{max}}.', { max: new Intl.NumberFormat('pt-BR').format(cappedMax) })); } catch (_) { void 0; }
      }
      return prev
        .map(ci => ci.id === id ? { ...ci, count: finalCount } : ci)
        .filter(ci => ci.count > 0);
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const persistGuestCart = (items) => {
    try {
      if (items.length === 0) {
        localStorage.removeItem(guestCartKey);
      } else {
        localStorage.setItem(guestCartKey, JSON.stringify(items));
      }
    } catch (e) {
      console.warn('Failed to persist guest cart locally', e);
    }
  };

  const scheduleRemoteSave = (items) => {
    // Only attempt remote save when fully logged in with JWT
    if (!accountContext.isLoggedIn || !accountContext.jwt) return;
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(async () => {
      if (items.length === 0) {
        try { await storageUnit.setData({ cartItems: [] }); } catch(e) { /* silent */ }
        return;
      }
      try {
        await storageUnit.setData({ cartItems: items });
      } catch (err) {
        if (err?.statusCode !== 401) console.error('Cart remote save failed', err);
      }
    }, 420); // debounced ~0.4s
  };

  const loadCard = async () => {
    // If we don't have a JWT, avoid remote load and use guest cart
    if (!accountContext.isLoggedIn || !accountContext.jwt) {
      try {
        const guestRaw = localStorage.getItem(guestCartKey);
        if (!guestRaw) return;
        const guestItems = JSON.parse(guestRaw);
        if (Array.isArray(guestItems)) {
          setCartItems(normalizeCartItems(guestItems));
        }
      } catch (e) {
        console.warn('Failed to load guest cart', e);
      }
      return;
    }

    // Fully logged-in with JWT: sync with remote storage
    await storageUnit.flush();
    const data = storageUnit.data;
    try {
      const items = data.cartItems;
      if (!items || items.length === 0) {
        // if remote empty but guest has items, migrate them
        const guestRaw = localStorage.getItem(guestCartKey);
        if (guestRaw) {
          const guestItems = JSON.parse(guestRaw);
          if (Array.isArray(guestItems) && guestItems.length > 0) {
            const normalizedGuestItems = normalizeCartItems(guestItems);
            setCartItems(normalizedGuestItems);
            try { await storageUnit.setData({ cartItems: normalizedGuestItems }); } catch (e) {
              console.error('Failed to migrate guest cart to remote', e);
            }
            localStorage.removeItem(guestCartKey);
            return;
          }
        }
        // Keep current cartItems state; do not overwrite with empty
        return;
      }
      setCartItems(normalizeCartItems(items));
    } catch (err) {
      // Keep current cartItems state on errors
    }
  };

  useEffect(() => {
    loadCard();
  }, [accountContext.isLoggedIn, accountContext.jwt]);

  useEffect(() => {
    if (accountContext.isLoggedIn && accountContext.jwt) {
      scheduleRemoteSave(cartItems);
    } else {
      persistGuestCart(cartItems);
    }
  }, [cartItems, accountContext.isLoggedIn, accountContext.jwt]);

  // If user attempted checkout while not logged in, redirect to checkout immediately after login
  useEffect(() => {
    if (!accountContext.isLoggedIn || !accountContext.jwt) return;
    try {
      const flag = localStorage.getItem(pendingCheckoutKey);
      if (flag === 'true' && cartItems.length > 0) {
        localStorage.removeItem(pendingCheckoutKey);
        window.location.assign('/checkout');
      }
    } catch (e) {
      // ignore storage errors
    }
  }, [accountContext.isLoggedIn, accountContext.jwt, cartItems.length]);

  return (
    <cartContex.Provider
      value={{
        addToCart,
        removeFromCart,
        updateItemCount,
        getEffectiveMaxForItem,
        getItemUnitPrice,
        clearCart,
        cartItems,
        // Mark checkout intent when user must log in first; call this before showing login
        markPendingCheckout() {
          try { localStorage.setItem(pendingCheckoutKey, 'true'); } catch (e) {
            // ignore storage errors
          }
        },
        get totalItemCount() {
          return cartItems.reduce((a, c) => a + c.count, 0);
        },
        get totalCost() {
          return cartItems.reduce((a, c) => a + getItemUnitPrice(c) * c.count, 0);
        },
      }}
    >
      {children}
    </cartContex.Provider>
  );
}

// const cart = useCart() to use this context
export function useCart() {
  return useContext(cartContex);
}
